import React, { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check as CheckIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  ViewColumn as ViewColumnIcon,
} from '@mui/icons-material';
import { button } from '@/shared/theme';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@/shared/ui';

export interface ListToolbarMoreMenuItem {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  /** Shows a check and keeps the item highlighted. */
  selected?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

export interface ListToolbarMoreMenuProps {
  onRefresh: () => void;
  /** Disables Làm mới while a background refetch is in flight. */
  refreshing?: boolean;
  onColumns?: () => void;
  columnsDisabled?: boolean;
  /** Extra actions after Làm mới and Tùy chỉnh cột. */
  items?: ListToolbarMoreMenuItem[];
  /** Extra panel under the actions. Receives close so a choice can dismiss the menu. */
  children?: (close: () => void) => ReactNode;
}

/**
 * Overflow (⋯) on a list toolbar. Holds refresh and the column layout action
 * so the toolbar keeps the primary button visible.
 */
const ListToolbarMoreMenu: React.FC<ListToolbarMoreMenuProps> = ({
  onRefresh,
  refreshing = false,
  onColumns,
  columnsDisabled = false,
  items,
  children,
}) => {
  const { t } = useTranslation('common');
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const close = (): void => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        aria-label={t('moreActions')}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        size="small"
        sx={{
          bgcolor: 'common.white',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          height: button.height,
          width: button.height,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          disabled={refreshing}
          onClick={() => {
            close();
            onRefresh();
          }}
        >
          <ListItemIcon>
            <RefreshIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('refresh')}</ListItemText>
        </MenuItem>
        {onColumns ? (
          <MenuItem
            disabled={columnsDisabled}
            onClick={() => {
              close();
              onColumns();
            }}
          >
            <ListItemIcon>
              <ViewColumnIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('table.customizeColumns')}</ListItemText>
          </MenuItem>
        ) : null}
        {items?.map((item) => (
          <MenuItem
            key={item.id}
            disabled={item.disabled}
            selected={item.selected}
            onClick={() => {
              close();
              item.onClick();
            }}
          >
            <ListItemIcon>
              {item.icon ?? (item.selected ? <CheckIcon fontSize="small" /> : null)}
            </ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
        {children ? children(close) : null}
      </Menu>
    </>
  );
};

export default ListToolbarMoreMenu;
